import { useMemo, useState } from "react"
import axios from "axios"
import toast from "react-hot-toast"

const formatNumber = (n) => Number(n || 0).toLocaleString("id-ID")

export default function ImportModal({ freezeId, checkId, onClose, onSuccess }) {
  const [fileName, setFileName] = useState("")
  const [parsedRows, setParsedRows] = useState([])
  const [parseError, setParseError] = useState("")
  const [parsing, setParsing] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [mode, setMode] = useState("set")

  const validRows = useMemo(
    () => parsedRows.filter((r) => r.product_unit_id && Number.isFinite(r.quantity) && r.quantity >= 0),
    [parsedRows]
  )
  const fillableRows = useMemo(() => validRows.filter((r) => r.quantity > 0), [validRows])
  const skipRows = useMemo(
    () => parsedRows.filter((r) => !r.product_unit_id || !Number.isFinite(r.quantity)),
    [parsedRows]
  )

  const handleFile = async (file) => {
    if (!file) return
    setFileName(file.name)
    setParsing(true)
    setParseError("")
    setParsedRows([])
    try {
      const fd = new FormData()
      fd.append("file", file)
      const res = await axios.post(
        `${process.env.REACT_APP_INVENTORY_API_URL}/stock-opname/${freezeId}/checks/${checkId}/import/parse`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      )
      const items = (res.data?.data?.items || []).map((it) => ({
        product_unit_id: String(it.product_unit_id || "").trim(),
        product_unit_name: String(it.product_unit_name || ""),
        quantity: it.quantity === null || it.quantity === undefined ? NaN : Number(it.quantity),
        notes: String(it.notes || ""),
        row_index: it.row_index,
      }))
      setParsedRows(items)
    } catch (err) {
      const msg =
        err?.response?.data?.errors?.file?.[0] ||
        err?.response?.data?.message ||
        err?.message ||
        "Gagal mem-parse file"
      setParseError(msg)
    } finally {
      setParsing(false)
    }
  }

  const submit = async () => {
    if (fillableRows.length === 0) {
      toast.error("Tidak ada baris valid (qty > 0) untuk di-import.")
      return
    }
    setSubmitting(true)
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_INVENTORY_API_URL}/stock-opname/${freezeId}/checks/${checkId}/items/bulk`,
        {
          mode,
          items: fillableRows.map((r) => ({
            product_unit_id: r.product_unit_id,
            quantity: r.quantity,
            notes: r.notes || undefined,
          })),
        }
      )
      const data = res.data?.data || {}
      const summary =
        Number(data.added) >= 0 || Number(data.updated) >= 0
          ? `${data.added || 0} item baru, ${data.updated || 0} item di-update`
          : res.data?.message || `${fillableRows.length} item di-import`
      toast.success(summary, { duration: 5000 })
      // Pastikan parent refetch SEBELUM modal close supaya list ke-update
      if (onSuccess) {
        try {
          await onSuccess()
        } catch (e) {
          // ignore — sukses simpan, refresh fail OK
        }
      }
      onClose?.()
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Import gagal")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center bg-black/50 sm:p-4"
      onClick={onClose}
    >
      <div
        className="w-full h-full sm:h-auto sm:max-w-3xl sm:max-h-[90vh] bg-white sm:rounded-2xl shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
          <div>
            <h3 className="text-base font-semibold text-neutral-900">Import Excel</h3>
            <p className="text-xs text-neutral-500 mt-0.5">
              Unggah file Excel hasil cek stock (.xlsx / .xls / .csv). Format kolom: Product Unit ID, Nama Produk, Qty Fisik, Catatan.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-neutral-500 hover:text-neutral-900 rounded-lg hover:bg-neutral-100"
          >
            ✕
          </button>
        </div>

        <div className="p-5 border-b border-neutral-200 bg-neutral-50 space-y-3">
          <label className="block">
            <span className="text-sm font-medium text-neutral-700">File Excel</span>
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={(e) => handleFile(e.target.files?.[0])}
              className="block w-full mt-1 text-sm text-neutral-700 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-brand-900 file:text-white hover:file:bg-brand-800"
            />
          </label>

          <div>
            <span className="text-sm font-medium text-neutral-700">Mode</span>
            <div className="flex flex-col gap-2 mt-1 sm:flex-row">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="mode"
                  value="set"
                  checked={mode === "set"}
                  onChange={(e) => setMode(e.target.value)}
                />
                <span><strong>SET</strong> — qty Excel menggantikan qty yang ada (direkomendasikan)</span>
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="mode"
                  value="sum"
                  checked={mode === "sum"}
                  onChange={(e) => setMode(e.target.value)}
                />
                <span><strong>SUM</strong> — qty Excel ditambah ke qty yang ada</span>
              </label>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {parsing ? (
            <div className="p-10 text-sm text-center text-neutral-500">Memuat Excel...</div>
          ) : parseError ? (
            <div className="p-10 text-sm text-center text-rose-700">{parseError}</div>
          ) : parsedRows.length === 0 ? (
            <div className="p-10 text-sm text-center text-neutral-500">
              {fileName ? "Tidak ada baris terbaca dari file." : "Pilih file Excel untuk pratinjau."}
            </div>
          ) : (
            <div>
              <div className="px-5 py-3 border-b border-neutral-200 bg-white text-sm text-neutral-700">
                <strong>{parsedRows.length}</strong> baris terbaca ·{" "}
                <span className="text-emerald-700">{fillableRows.length} akan di-import (qty&gt;0)</span>
                {skipRows.length > 0 && (
                  <span className="text-rose-700"> · {skipRows.length} dilewati (tidak valid)</span>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-neutral-50">
                    <tr className="text-left text-neutral-600">
                      <th className="px-3 py-2 font-medium">#</th>
                      <th className="px-3 py-2 font-medium">Product Unit ID</th>
                      <th className="px-3 py-2 font-medium">Nama Produk</th>
                      <th className="px-3 py-2 font-medium text-right">Qty</th>
                      <th className="px-3 py-2 font-medium">Catatan</th>
                      <th className="px-3 py-2 font-medium text-right">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedRows.map((r, idx) => {
                      const isValid = r.product_unit_id && Number.isFinite(r.quantity)
                      const willImport = isValid && r.quantity > 0
                      return (
                        <tr key={idx} className="border-t border-neutral-100">
                          <td className="px-3 py-2 text-neutral-500">{r.row_index || idx + 1}</td>
                          <td className="px-3 py-2 font-mono text-xs">{r.product_unit_id || "—"}</td>
                          <td className="px-3 py-2 text-neutral-700">{r.product_unit_name || "-"}</td>
                          <td className="px-3 py-2 text-right">
                            {Number.isFinite(r.quantity) ? (
                              formatNumber(r.quantity)
                            ) : (
                              <span className="text-rose-600">tidak valid</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-neutral-600">{r.notes || "-"}</td>
                          <td className="px-3 py-2 text-right">
                            {willImport ? (
                              <span className="px-2 py-0.5 text-xs rounded bg-emerald-50 text-emerald-700 border border-emerald-200">Import</span>
                            ) : isValid && r.quantity === 0 ? (
                              <span className="px-2 py-0.5 text-xs rounded bg-neutral-50 text-neutral-600 border border-neutral-200">Lewati (qty 0)</span>
                            ) : (
                              <span className="px-2 py-0.5 text-xs rounded bg-rose-50 text-rose-700 border border-rose-200">Tidak valid</span>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-neutral-200 bg-white flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm text-neutral-700">
            {fillableRows.length > 0 ? (
              <>
                Akan import <strong>{fillableRows.length}</strong> item · mode <strong className="uppercase">{mode}</strong>
              </>
            ) : (
              <span className="text-neutral-500">Pilih file lalu cek pratinjau.</span>
            )}
          </div>
          <div className="flex items-center gap-2 sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="flex-1 sm:flex-initial px-4 py-2 text-sm font-medium text-neutral-700 border rounded-lg border-neutral-300 bg-white"
            >
              Batal
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={submitting || fillableRows.length === 0}
              className="flex-1 sm:flex-initial px-4 py-2 text-sm font-semibold text-white rounded-lg bg-brand-900 hover:bg-brand-800 disabled:opacity-50"
            >
              {submitting ? "Memproses..." : `Import ${fillableRows.length > 0 ? `${fillableRows.length} item` : "item"}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
